import { InputField, SelectSimple } from '@advanza/input'
import { SelectSimpleChangeEvent } from '@advanza/input/interfaces/SelectSimpleChangeEvent'
import { Button } from '@advanza/ui'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import BaseModal, {
    BaseModalFooter,
    BaseModalHeader,
    BaseModalMainContent,
} from '@trustoo/components/modal/BaseModal'
import { fetchEligiblePromos } from 'actions/providers'
import { useModalToast } from 'components/misc/Toaster'
import { useAppDispatch } from 'hooks/hooks'
import { PROMO_SOURCE_OFFICE, STATUS_PENDING } from 'interfaces/ProviderPromos'
import { useEffect, useState } from 'react'

interface PromoOption {
    promo_id: number
    name: string
    identifier: string
    description?: string | null
    expiry_date?: string | null
    offers_info?: Array<{
        'one-time-leads'?: {
            value?: number
            type?: string
            expiryDate?: string
        }
        'recurring-leads'?: {
            value?: number
            type?: string
            promo_length?: number
        }
        'subscription-discount'?: {
            value?: number
            type?: string
            promo_length?: number
        }
        'subscription-discount-fixed-date'?: {
            value?: number
            type?: string
            endDate?: string
        }
    }> | null
}

interface OfferDetail {
    value?: number
    type?: string
    promo_length?: number
    expiryDate?: string
    endDate?: string
}

interface AddProviderPromoModalProps {
    providerId: number
    providerName?: string
    onConfirm: (data: {
        promo_id?: number | null
        type?: string | null
        source?: string | null
        start_date?: string | null
        expiry_date?: string | null
        status: string
    }) => Promise<void>
    onCancel: () => void
}

const AddProviderPromoModal = NiceModal.create(
    ({ providerId, providerName, onConfirm, onCancel }: AddProviderPromoModalProps) => {
        const modal = useModal()
        const dispatch = useAppDispatch()
        const [promos, setPromos] = useState<PromoOption[]>([])
        const [isLoadingPromos, setIsLoadingPromos] = useState(true)
        const [selectedPromoId, setSelectedPromoId] = useState<number | null>(null)
        const [startDate, setStartDate] = useState<string>('')
        const [expiryDate, setExpiryDate] = useState<string>('')
        const { addModalToast } = useModalToast()

        // Fetch eligible promos on mount
        useEffect(() => {
            const loadPromos = async () => {
                try {
                    setIsLoadingPromos(true)
                    const fetchedPromos = await dispatch(fetchEligiblePromos(providerId))
                    setPromos(fetchedPromos)
                    // Select first promo by default if available
                    if (fetchedPromos.length > 0) {
                        setSelectedPromoId(fetchedPromos[0].promo_id)
                    }
                } catch (error) {
                    const message =
                        typeof error === 'object' &&
                        error !== null &&
                        'message' in error &&
                        typeof error.message === 'string'
                            ? error.message
                            : 'Failed to load promos'
                    addModalToast({ id: 'load-error', msg: message, icon: 'error', red: true })
                } finally {
                    setIsLoadingPromos(false)
                }
            }
            loadPromos()
        }, [dispatch, providerId])

        // Update fields when promo is selected
        useEffect(() => {
            if (selectedPromoId && promos.length > 0) {
                const selectedPromo = promos.find((p) => p.promo_id === selectedPromoId)
                if (selectedPromo) {
                    setStartDate('')
                    // Set expiry date from promo if present, otherwise leave empty
                    if (selectedPromo.expiry_date) {
                        // Convert to datetime-local format (YYYY-MM-DDTHH:mm)
                        const expiryDateObj = new Date(selectedPromo.expiry_date)
                        const year = expiryDateObj.getFullYear()
                        const month = String(expiryDateObj.getMonth() + 1).padStart(2, '0')
                        const day = String(expiryDateObj.getDate()).padStart(2, '0')
                        const hours = String(expiryDateObj.getHours()).padStart(2, '0')
                        const minutes = String(expiryDateObj.getMinutes()).padStart(2, '0')
                        setExpiryDate(`${year}-${month}-${day}T${hours}:${minutes}`)
                    } else {
                        setExpiryDate('')
                    }
                }
            }
        }, [selectedPromoId, promos])

        const handlePromoChange = (event: SelectSimpleChangeEvent) => {
            setSelectedPromoId(Number(event.target.value))
        }

        const handleConfirm = async () => {
            try {
                const selectedPromo = promos.find((p) => p.promo_id === selectedPromoId)
                await onConfirm({
                    promo_id: selectedPromoId,
                    type: selectedPromo?.identifier || null,
                    source: PROMO_SOURCE_OFFICE, // Always office when adding from office
                    start_date: startDate || null,
                    expiry_date: expiryDate || null,
                    status: STATUS_PENDING, // Always set to pending when adding
                })
                modal.resolve()
                modal.hide()
            } catch (error) {
                addModalToast({
                    id: 'save-error',
                    msg: error instanceof Error ? error.message : 'Failed to add promo',
                    icon: 'error',
                    red: true,
                })
            }
        }

        const renderOffersPreview = (offersInfo: PromoOption['offers_info']) => {
            if (!offersInfo) {
                return <span style={{ color: '#999', fontStyle: 'italic' }}>No offers</span>
            }

            // Handle array format only (new format)
            if (!Array.isArray(offersInfo)) {
                return <span style={{ color: '#999', fontStyle: 'italic' }}>No offers</span>
            }

            if (offersInfo.length === 0) {
                return <span style={{ color: '#999', fontStyle: 'italic' }}>No offers</span>
            }

            const formattedOffers: string[] = []

            offersInfo.forEach((offerObj) => {
                if (typeof offerObj === 'object' && offerObj !== null) {
                    Object.keys(offerObj).forEach((offerType) => {
                        // Only handle new format: one-time-leads, recurring-leads, subscription-discount(-fixed-date)
                        if (
                            offerType !== 'one-time-leads' &&
                            offerType !== 'recurring-leads' &&
                            offerType !== 'subscription-discount' &&
                            offerType !== 'subscription-discount-fixed-date'
                        ) {
                            return // Skip old format offers
                        }

                        const offerData = offerObj[offerType as keyof typeof offerObj] as
                            | OfferDetail
                            | undefined
                        if (typeof offerData === 'object' && offerData !== null) {
                            const value =
                                offerData.value !== undefined && offerData.value !== null
                                    ? offerData.value
                                    : ''
                            const type = offerData.type || ''
                            const promoLength =
                                offerData.promo_length !== undefined &&
                                offerData.promo_length !== null
                                    ? offerData.promo_length
                                    : null
                            const expiryDate = offerData.expiryDate || ''

                            // Format based on offer type (new format only)
                            let offerText = ''
                            if (offerType === 'subscription-discount-fixed-date') {
                                let valueDisplay: string | number = value
                                if (type === 'percentage') {
                                    valueDisplay = `${value}%`
                                } else if (type === 'amount') {
                                    valueDisplay = `€${value}`
                                }
                                if (offerData.endDate) {
                                    const endDateObj = new Date(offerData.endDate)
                                    offerText = `${valueDisplay} discount until ${endDateObj.toLocaleDateString()}`
                                } else {
                                    offerText = `${valueDisplay} discount (no end date)`
                                }
                            } else if (offerType === 'subscription-discount') {
                                // Subscription discount
                                let valueDisplay = value
                                if (type === 'percentage') {
                                    valueDisplay = `${value}%`
                                } else if (type === 'amount') {
                                    valueDisplay = `€${value}`
                                }
                                if (promoLength !== null && promoLength !== undefined) {
                                    if (promoLength === 0) {
                                        offerText = `${valueDisplay} discount (one-time)`
                                    } else {
                                        offerText = `${valueDisplay} discount for ${promoLength} month${promoLength === 1 ? '' : 's'}`
                                    }
                                } else {
                                    offerText = `${valueDisplay} discount (ongoing)`
                                }
                            } else if (offerType === 'one-time-leads') {
                                // One-time free leads
                                let valueDisplay = value
                                if (type === 'percentage') {
                                    valueDisplay = `${value}%`
                                } else if (type === 'amount') {
                                    valueDisplay = `${value}`
                                }
                                if (expiryDate) {
                                    const expiryDateObj = new Date(expiryDate)
                                    const formattedDate = expiryDateObj.toLocaleDateString()
                                    offerText = `${valueDisplay} one-time leads (expires: ${formattedDate})`
                                } else {
                                    offerText = `${valueDisplay} one-time leads`
                                }
                            } else if (offerType === 'recurring-leads') {
                                // Recurring free leads
                                let valueDisplay = value
                                if (type === 'percentage') {
                                    valueDisplay = `${value}%`
                                } else if (type === 'amount') {
                                    valueDisplay = `${value}`
                                }
                                if (promoLength !== null && promoLength !== undefined) {
                                    offerText = `${valueDisplay} recurring leads for ${promoLength} month${promoLength === 1 ? '' : 's'}`
                                } else {
                                    offerText = `${valueDisplay} recurring leads (ongoing)`
                                }
                            }

                            if (offerText) {
                                formattedOffers.push(offerText)
                            }
                        }
                    })
                }
            })

            if (formattedOffers.length === 0) {
                return <span style={{ color: '#999', fontStyle: 'italic' }}>No offers</span>
            }

            return (
                <div className="space-y-1">
                    {formattedOffers.map((offer, index) => (
                        <div key={index} className="text-md">
                            {offer}
                        </div>
                    ))}
                </div>
            )
        }

        const handleCancel = () => {
            onCancel()
            modal.resolve()
            modal.hide()
        }

        return (
            <BaseModal onClose={handleCancel}>
                <BaseModalHeader>
                    <h2 className="text-display-xs font-bold">Add Promo</h2>
                </BaseModalHeader>
                <BaseModalMainContent>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-md font-medium font-bold text-neutral-700 mb-1">
                                Type *
                            </label>
                            {isLoadingPromos ? (
                                <div className="text-md text-neutral-500">Loading promos...</div>
                            ) : promos.length === 0 ? (
                                <div className="text-md text-red-500">
                                    No eligible promos available
                                </div>
                            ) : (
                                <SelectSimple
                                    value={selectedPromoId?.toString() || ''}
                                    onChange={handlePromoChange}
                                    disabled={false}
                                    options={promos.map((promo) => ({
                                        value: promo.promo_id.toString(),
                                        name: promo.name,
                                    }))}
                                />
                            )}
                        </div>
                        {selectedPromoId && (
                            <div>
                                <label className="block text-md font-medium text-neutral-700 mb-1">
                                    Offer Preview
                                </label>
                                <div className="p-3 bg-neutral-50 rounded border">
                                    {renderOffersPreview(
                                        promos.find((p) => p.promo_id === selectedPromoId)
                                            ?.offers_info,
                                    )}
                                </div>
                            </div>
                        )}
                        <div>
                            <label className="block text-md font-medium font-bold text-neutral-700 mb-1">
                                Start Date (optional)
                            </label>
                            <InputField
                                type="datetime-local"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                disabled={false}
                                readOnly={false}
                                placeholder="Leave empty to set to current date/time"
                            />
                            <p className="text-sm text-neutral-500 mt-1">
                                If empty, the start date will be set to the current date and time
                            </p>
                        </div>
                        <div>
                            <label className="block text-md font-medium font-bold text-neutral-700 mb-1">
                                Expiry Date
                            </label>
                            <InputField
                                type="datetime-local"
                                value={expiryDate}
                                onChange={(e) => setExpiryDate(e.target.value)}
                                disabled={false}
                                readOnly={false}
                                min={(() => {
                                    // Set minimum to current date/time or startDate, whichever is later
                                    const now = new Date()
                                    const nowString = now.toISOString().slice(0, 16) // Format: YYYY-MM-DDTHH:mm
                                    if (startDate) {
                                        const startDateObj = new Date(startDate)
                                        const startDateString = startDateObj
                                            .toISOString()
                                            .slice(0, 16)
                                        return startDateString > nowString
                                            ? startDateString
                                            : nowString
                                    }
                                    return nowString
                                })()}
                            />
                            <p className="text-sm text-neutral-500 mt-1">
                                Initial is promo expiry date. Clear the field to add this promo
                                without expiry date for {providerName || 'this provider'}
                            </p>
                        </div>
                    </div>
                </BaseModalMainContent>
                <BaseModalFooter>
                    <div className="flex justify-end gap-2">
                        <Button appearance="secondary" onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button
                            appearance="primary"
                            onClick={handleConfirm}
                            disabled={!selectedPromoId || isLoadingPromos || promos.length === 0}>
                            Add Promo
                        </Button>
                    </div>
                </BaseModalFooter>
            </BaseModal>
        )
    },
)

export default AddProviderPromoModal
